import { useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Checkbox,
  EmptyState,
  Modal,
  NativeSelect,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TabPanel,
  Tabs,
} from '@immo/ui-kit';
import type { ListOrganizationRecordsResponse } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';

export const RECORD_KINDS = ['deals', 'properties', 'contacts', 'leads'] as const;

export type RecordKind = (typeof RECORD_KINDS)[number];

export type RecordSelection = Readonly<Record<RecordKind, ReadonlyArray<string>>>;

const NO_SELECTION: RecordSelection = { deals: [], properties: [], contacts: [], leads: [] };
const TABS_ID = 'organization-records';

type Column<T> = { readonly label: string; readonly cell: (row: T) => ReactNode };

/** The review page's address for a selection: one repeated key per record kind (IMM-1104). */
export function moveReviewPath(
  organizationId: string,
  targetOrganizationId: string,
  selection: RecordSelection,
): string {
  const params = new URLSearchParams({ target: targetOrganizationId });

  for (const kind of RECORD_KINDS) for (const id of selection[kind]) params.append(kind, id);

  return `/workspaces/organizations/${organizationId}/move?${params.toString()}`;
}

/**
 * A Standort's deals, Objekte, Kontakte and Leads with one selection across the
 * four tabs, and the way into the move review (IMM-1104). `records` is null when
 * the list could not be loaded — said so, never shown as an empty Standort (BL-058).
 */
export function OrganizationRecords({
  organizationId,
  records,
}: {
  readonly organizationId: string;
  readonly records: ListOrganizationRecordsResponse | null;
}) {
  const t = useTranslations('pages.admin.organizations.records');
  const tDeals = useTranslations('pages.admin.deals.deleted');
  const tPropertyStatus = useTranslations('pages.admin.properties.status');
  const tLeadStatus = useTranslations('pages.workspace.leads.status');
  const intl = useIntl();
  const navigate = useNavigate();
  const [tab, setTab] = useState<RecordKind>('deals');
  const [selection, setSelection] = useState<RecordSelection>(NO_SELECTION);
  const [picking, setPicking] = useState(false);
  const [target, setTarget] = useState('');

  if (records === null) {
    return (
      <p role="alert" className="text-sm text-danger-600">
        {t('load-failed')}
      </p>
    );
  }

  const selectedCount = RECORD_KINDS.reduce((sum, kind) => sum + selection[kind].length, 0);
  const toggle = (kind: RecordKind, ids: ReadonlyArray<string>, checked: boolean) =>
    setSelection((current) => {
      const next = new Set(current[kind]);

      for (const id of ids) {
        if (checked) next.add(id);
        else next.delete(id);
      }

      return { ...current, [kind]: [...next] };
    });

  const closePicker = () => {
    setPicking(false);
    setTarget('');
  };

  const table = <T extends { readonly id: string }>(
    kind: RecordKind,
    rows: ReadonlyArray<T>,
    rowLabel: (row: T) => string,
    columns: ReadonlyArray<Column<T>>,
  ) => {
    if (rows.length === 0) return <EmptyState title={t(`empty.${kind}`)} />;

    const chosen = new Set(selection[kind]);
    const allChosen = rows.every((row) => chosen.has(row.id));
    const someChosen = rows.some((row) => chosen.has(row.id));

    return (
      <Table flush>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">
              <Checkbox
                checked={allChosen ? true : someChosen ? 'indeterminate' : false}
                onCheckedChange={(checked) =>
                  toggle(
                    kind,
                    rows.map((row) => row.id),
                    checked,
                  )
                }
                aria-label={t('select-all')}
              />
            </TableHead>
            {columns.map((column) => (
              <TableHead key={column.label}>{column.label}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell>
                <Checkbox
                  checked={chosen.has(row.id)}
                  onCheckedChange={(checked) => toggle(kind, [row.id], checked)}
                  aria-label={t('select-row', { label: rowLabel(row) })}
                />
              </TableCell>
              {columns.map((column) => (
                <TableCell key={column.label}>{column.cell(row)}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  const panels: Record<RecordKind, ReactNode> = {
    deals: table('deals', records.deals, (deal) => deal.propertyLabel ?? deal.id, [
      { label: t('columns.object'), cell: (deal) => deal.propertyLabel ?? '—' },
      { label: t('columns.phase'), cell: (deal) => tDeals(`phase.${deal.phase}`) },
      { label: t('columns.status'), cell: (deal) => tDeals(`status.${deal.status}`) },
      {
        label: t('columns.responsible'),
        cell: (deal) =>
          deal.responsibleMemberNames.length === 0 ? t('unassigned') : deal.responsibleMemberNames.join(', '),
      },
    ]),
    properties: table('properties', records.properties, (property) => property.objectNumber, [
      { label: t('columns.object-number'), cell: (property) => property.objectNumber },
      { label: t('columns.address'), cell: (property) => property.formattedAddress ?? property.title },
      { label: t('columns.status'), cell: (property) => tPropertyStatus(property.status) },
    ]),
    contacts: table('contacts', records.contacts, (contact) => contact.displayName, [
      { label: t('columns.name'), cell: (contact) => contact.displayName },
      { label: t('columns.type'), cell: (contact) => t(`contact-type.${contact.contactType}`) },
    ]),
    leads: table('leads', records.leads, (lead) => lead.ownerName, [
      { label: t('columns.owner'), cell: (lead) => lead.ownerName },
      { label: t('columns.status'), cell: (lead) => tLeadStatus(lead.status) },
      { label: t('columns.received'), cell: (lead) => intl.formatDate(lead.receivedAt, { dateStyle: 'medium' }) },
    ]),
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-sm text-text-secondary">{t('selected', { count: selectedCount })}</span>
        <Button
          size="sm"
          type="button"
          disabled={selectedCount === 0 || records.targets.length === 0}
          onClick={() => setPicking(true)}
        >
          {t('move-cta')}
        </Button>
      </div>
      {records.targets.length === 0 && <p className="text-sm text-text-tertiary">{t('no-targets')}</p>}
      <Tabs
        idBase={TABS_ID}
        active={tab}
        onChange={setTab}
        tabs={RECORD_KINDS.map((kind) => ({ key: kind, label: t(`tabs.${kind}`), count: records[kind].length }))}
      />
      <TabPanel idBase={TABS_ID} tabKey={tab}>
        {panels[tab]}
      </TabPanel>
      {picking && (
        <Modal
          open
          onClose={closePicker}
          confirmOnClose={false}
          size="sm"
          title={t('target.title')}
          footer={
            <>
              <Button variant="ghost" type="button" onClick={closePicker}>
                {t('target.cancel')}
              </Button>
              <Button
                type="button"
                disabled={target === ''}
                onClick={() => navigate(moveReviewPath(organizationId, target, selection))}
              >
                {t('target.cta')}
              </Button>
            </>
          }
        >
          <NativeSelect
            value={target}
            onChange={setTarget}
            placeholder={t('target.placeholder')}
            aria-label={t('target.label')}
            options={records.targets.map((candidate) => ({ value: candidate.id, label: candidate.name }))}
          />
        </Modal>
      )}
    </div>
  );
}
